// Two-factor authentication for the signed-in person's own account (W23), in both apps: whether it is on, turning it
// on (scan the QR code or type the key, then confirm with a code from the app) and off (password and a current code).
// The pages give it their own class names; the wording is the same everywhere.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { api, BffError, GENERIC_ERROR } from './bff';
import { PasswordField } from './PasswordInput';

interface Setup {
  secret: string;
  uri: string;
  qrSvg: string;
}

export interface TwoFactorClasses {
  /** A labelled field (the class the page's own labels carry). */
  field: string;
  primary: string;
  secondary: string;
}

const errorText = (err: unknown) => (err instanceof BffError ? err.message : GENERIC_ERROR);
/** "JBSW Y3DP EHPK 3PXP": a key typed by hand is typed in groups. */
export const grouped = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim();

export function TwoFactorSettings({ classes }: { classes: TwoFactorClasses }) {
  const client = useQueryClient();
  const status = useQuery({
    queryKey: ['two-factor'],
    queryFn: () => api<{ enabled: boolean }>('GET', '/auth/2fa'),
  });
  const [mode, setMode] = useState<'idle' | 'setup' | 'off'>('idle');
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [outcome, setOutcome] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const done = async (message: string) => {
    setMode('idle');
    setSetup(null);
    setCode('');
    setPassword('');
    setOutcome({ ok: true, message });
    await client.invalidateQueries({ queryKey: ['two-factor'] });
  };
  async function run(work: () => Promise<void>) {
    setBusy(true);
    try {
      await work();
    } catch (err) {
      setOutcome({ ok: false, message: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  const begin = () =>
    run(async () => {
      const { secret, otpauthUrl } = await api<{ secret: string; otpauthUrl: string }>('POST', '/auth/2fa/totp/init');
      const qrSvg = await QRCode.toString(otpauthUrl, { type: 'svg', margin: 0, width: 180 });
      setSetup({ secret, uri: otpauthUrl, qrSvg });
      setOutcome(null);
      setMode('setup');
    });
  const turnOn = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/auth/2fa/totp/enable', { body: { code } });
      await done('Two-factor authentication is on. You will be asked for a code when you sign in.');
    });
  };
  const turnOff = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/auth/2fa/totp/disable', { body: { password, code } });
      await done('Two-factor authentication is off.');
    });
  };
  const cancel = () => {
    setMode('idle');
    setSetup(null);
    setCode('');
    setPassword('');
    setOutcome(null);
  };

  const codeField = (
    <label className={classes.field}>
      <span>Authentication code</span>
      <input
        name="TwoFactorCode"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6-digit code"
        maxLength={7}
        required
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
    </label>
  );

  if (!status.data) return null;
  const enabled = status.data.enabled;
  return (
    <div>
      <p>
        Two-factor authentication is <strong>{enabled ? 'on' : 'off'}</strong>.{' '}
        {enabled
          ? 'When you sign in you are asked for a code from your authenticator app, unless the device was remembered.'
          : 'Turn it on to be asked for a code from an authenticator app (Google Authenticator, Authy and others) when you sign in.'}
      </p>
      {outcome && (
        <p role={outcome.ok ? 'status' : 'alert'} style={{ fontWeight: 700 }}>
          {outcome.message}
        </p>
      )}

      {mode === 'idle' && !enabled && (
        <button type="button" className={classes.primary} disabled={busy} onClick={() => void begin()}>
          Turn on two-factor authentication
        </button>
      )}
      {mode === 'idle' && enabled && (
        <button
          type="button"
          className={classes.secondary}
          disabled={busy}
          onClick={() => {
            setOutcome(null);
            setMode('off');
          }}
        >
          Turn off two-factor authentication
        </button>
      )}

      {mode === 'setup' && setup && (
        <form onSubmit={turnOn}>
          <ol style={{ paddingLeft: 20 }}>
            <li>Open your authenticator app and add an account.</li>
            <li>
              Scan this QR code, or type the key by hand:
              <div
                role="img"
                aria-label="QR code for your authenticator app"
                style={{ background: '#fff', borderRadius: 8, display: 'inline-block', margin: '12px 0', padding: 10 }}
                dangerouslySetInnerHTML={{ __html: setup.qrSvg }}
              />
              <p>
                Key: <code data-testid="two-factor-key">{grouped(setup.secret)}</code>
              </p>
            </li>
            <li>Enter the 6-digit code the app shows.</li>
          </ol>
          {codeField}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button type="submit" className={classes.primary} disabled={busy}>
              Confirm and turn on
            </button>
            <button type="button" className={classes.secondary} disabled={busy} onClick={cancel}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {mode === 'off' && (
        <form onSubmit={turnOff}>
          <p>To turn it off, enter your password and a current code from your authenticator app.</p>
          <PasswordField
            className={classes.field}
            label="Password"
            name="TwoFactorPassword"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {codeField}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button type="submit" className={classes.primary} disabled={busy}>
              Turn off
            </button>
            <button type="button" className={classes.secondary} disabled={busy} onClick={cancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
